import { isUndefined } from 'util';
import { OFF } from '../components/TimePickerNavbar/TimeOption';
import { TimePickerRange } from '../components/TimePickerNavbar/model'
import DateType from '../components/TimePickerNavbar/DateType'
import { Labels } from '../common/labels/model'

export type PostMessage<T> = {
  message: string
  content?: T
}

export const isPostMessage = <T>(data: any, message: string): data is PostMessage<T> => {
  return (data as PostMessage<T>).message === message
}

class VisualizerApi {

  private timers: {[key: string]: any} = {};
  private listeners: {[key: string]: any} = {};
  private postMessageListeners: {[key: string]: any} = {};
  
  static iFramePostMessage<T>(iFrame: HTMLIFrameElement, postMessage: PostMessage<T>) {
    iFrame.contentWindow && iFrame.contentWindow.postMessage(postMessage, '*')
  }
  
  addMessageEventListener<T>(message: string, callback: (content?: T) => void) {
    const fn = (messageEvent: MessageEvent) => {
      if (isPostMessage<T>(messageEvent.data, message)) callback(messageEvent.data.content)
    }
    
    window.addEventListener('message', fn)
    this.postMessageListeners[message] = fn
  }
  
  removeMessageEventListener<T>(message: string) {
    const fn = this.postMessageListeners[message]
    
    if (fn) {
      window.removeEventListener('message', fn)
      delete this.postMessageListeners[message]
    }
  }

  callWhenReady(nameSpace: string, fn: (detail?: any) => void) {
    if (this.existsFrameApi()) {
      fn();
    } else {
      // wait until document is ready
      if (this.timers[nameSpace]) clearTimeout(this.timers[nameSpace]);
      this.timers[nameSpace] = setTimeout(() => {
        this.callWhenReady(nameSpace, fn);
      },                                  100)
    }
  }

  addListener(event: string, fn: (detail?: any) => void) {
    if (this.documentsReady()) {
      const documents = this.documents()
      documents.forEach(iframeDocument => iframeDocument.addEventListener(event, fn))
      this.listeners[event] = fn;
    } else {
      // wait until document is ready
      if (this.timers[event]) clearTimeout(this.timers[event]);
      this.timers[event] = setTimeout(() => { this.addListener(event, fn); },  100);
    }
  }

  removeListener(event: string, fn?: (detail?: any) => void) {
    if (this.timers[event]) clearTimeout(this.timers[event]);

    if (fn || this.listeners[event]) {
      const documents = this.documents()
      documents.forEach(iframeDocument => iframeDocument.removeEventListener(event, this.listeners[event]))
    }

    delete this.timers[event];
    delete this.listeners[event]
  }

  existFrames() {
    const iframes = this.iframes()

    for (let i = 0; i < iframes.length; i++) {
      if (isUndefined(iframes[i])) {
        return false
      }
    }

    return true
  }

  existsFrameApi() {
    const apis = this.apis()

    if (apis.length === 0) {
      return false
    }

    for (let i = 0; i < apis.length; i++) {
      if (isUndefined(apis[i])) {
        return false
      }
    }

    return true
  }

  range(range: TimePickerRange) {
    const apis = this.apis()
    let result;
    let index;

    for (let i = 0; i < apis.length; i++) {
      const visualizerRange = apis[i].datePickerRange(range.from, range.to, range.refresh === OFF ? undefined : range.refresh)
      if (range.previousFrom && range.previousTo) {
        let visualizerFrom = DateType.formatVisualizerTime(visualizerRange.from);
        let visualizerTo = DateType.formatVisualizerTime(visualizerRange.to);
        if ((range.previousFrom !== visualizerFrom || range.previousTo !== visualizerTo) && (visualizerFrom !== 'now-30m' )) {
          result = visualizerRange
          index = i;
          break;
        }
      } else {
        result = visualizerRange
      }
    }

    if (index) {
      for (let i = 0; i < apis.length; i++) {
        if (i !== index) {
          apis[i].datePickerRange(result.from, result.to, range.refresh === OFF ? undefined : range.refresh)
        }
      }
    }

    return result
  }

  currentDashboard() {
    const apis = this.apis()
    if (apis.length > 0) {
      return apis[0].currentDashboard()
    } else {
      return undefined
    }
  }

  refresh() {
    const apis = this.apis()
    apis.forEach(api => api.refreshDashboard())
  }

  starDashboard() {
    const apis = this.apis()
    if (apis.length > 0) {
      return apis[0].starDashboard()
    } else {
      return undefined
    }
  }

  zoomOut() {
    const apis = this.apis()
    let result;

    for (let i = 0; i < apis.length; i++) {
      if (i === 0) result = apis[i].zoom(2)
      else apis[i].zoom(2)
    }

    return result
  }

  move(direction: number) {
    const apis = this.apis()
    if (apis.length > 0) {
      return apis[0].moveToDirection(direction)
    } else {
      return undefined
    }
  }

  duplicate() {
    const apis = this.apis()
    if (apis.length > 0) {
      return apis[0].duplicate()
    } else {
      return undefined
    }
  }

  save() {
    const apis = this.apis()
    if (apis.length > 0) {
      apis[0].save()
    }
  }

  clone(dashboardName: string, overwrite?: boolean, envId?: string) {
    const apis = this.apis()
    if (apis.length > 0) {
      apis[0].clone(dashboardName, overwrite, envId)
    }
  }

  export(data: object, panel: object, mode: string, dateTimeFormat: string, excel: boolean) {
    const apis = this.apis()
    if (apis.length > 0) {
      apis[0].exportCSV(data, panel, mode, dateTimeFormat, excel)
    }
  }

  openEditView(view: string) {
    const documents = this.documents()
    const apis = this.apis()
    if (documents.length > 0 && apis.length > 0) {
      apis[0].editView(view)
      const editViews = documents[0].getElementsByClassName('dashboard-container')
      if (editViews.length > 0) editViews[0].scrollIntoView()
    }
  }

  hideEditorDashboardPanel() {
    const apis = this.apis()
    if (apis.length > 0) {
      apis[0].hideDashboardEditor()
    }
  }

  share(){
    const apis = this.apis()
    if (apis.length > 0) {
      apis[0].shareDashboard()
    }
  }

  deleteDashboard(){
    const apis = this.apis()
    if (apis.length > 0) {
      return apis[0].deleteDashboard()
    }
  }

  hasChanges() {
    const apis = this.apis()
    if (apis.length > 0) {
      return apis[0].hasUnsavedChanges()
    }
  }

  showUnsavedChangedModal() {
    const apis = this.apis()
    if (apis.length > 0) {
      apis[0].showUnsavedModal()
    }
  }

  getOriginalDashboard() {
    const apis = this.apis()
    if (apis.length > 0) {
      return apis[0].getUnsavedOriginalDashboard()
    } else {
      return undefined
    }
  }

  saveLabels(labels: Labels) {
    const apis = this.apis()
    apis.forEach(api => api.saveLabelsObj(labels))
  }

  private iframes() {
    const elems = Array.from(document.querySelectorAll('.iframe-container iframe')) as HTMLIFrameElement[];
    return elems.map(iframe => iframe.contentWindow || iframe.contentDocument)
  }

  private apis() {
    const iframes = this.iframes()
    return iframes.map((iframe: any) => iframe.api || iframe.api).filter(api => !isUndefined(api))
  }

  private documents() {
    const iframes = this.iframes()
    return iframes.map((iframe: any) => iframe.document || iframe.document)
  }

  private documentsReady() {
    const documents = this.documents()

    if (documents.length === 0) {
      return false
    }

    for (let i = 0; i < documents.length; i++) {
      if (!(documents[i].body && documents[i].body.hasChildNodes())) {
        return false
      }
    }

    return true
  }

}

export default new VisualizerApi()
